
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PenTool, Save, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import WritingInterface from "@/components/writing/WritingInterface";
import TopicSelector from "@/components/writing/TopicSelector";
import { WritingSession } from "@/types/writing";
import Seo from "@/components/seo/Seo";

const WritingPad = () => {
  const { grade } = useParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [writingSession, setWritingSession] = useState<WritingSession | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<string>("");
  const [selectedSubject, setSelectedSubject] = useState<string>("General Writing");
  const [selectedWritingType, setSelectedWritingType] = useState<string>("Creative");
  const [showTopicSelector, setShowTopicSelector] = useState(true);

  const handleSave = async () => {
    if (!user) {
      toast({
        title: "Please sign in",
        description: "You need to sign in to save your writing.",
        variant: "destructive",
      });
      return;
    }

    if (!title.trim() || !content.trim()) {
      toast({
        title: "Missing content",
        description: "Please add both a title and content before saving.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      // For now, we'll just save locally or show success
      // In a real implementation, you'd save to a database
      toast({
        title: "Saved successfully!",
        description: "Your writing has been saved.",
      });
      
      setTitle("");
      setContent("");
    } catch (error) {
      console.error('Error saving writing:', error);
      toast({
        title: "Error saving",
        description: "There was a problem saving your writing. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleWritingSubmit = (session: WritingSession) => {
    setWritingSession(session);
  };

  const handleStartNew = () => {
    setWritingSession(null);
    setContent("");
    setTitle("");
    setShowTopicSelector(true);
    setSelectedTopic("");
  };

  const handleTopicSelect = (topic: string, subject: string, writingType: string, selectedGrade: string) => {
    console.log('Topic selected:', { topic, subject, writingType, selectedGrade });
    setSelectedTopic(topic);
    setSelectedSubject(subject);
    setSelectedWritingType(writingType);
    setShowTopicSelector(false);
  };

  const handleBackToTopicSelection = () => {
    setShowTopicSelector(true);
    setSelectedTopic("");
    setWritingSession(null);
  };

  return (
    <div className="min-h-full bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Seo
        title={grade ? `Grade ${grade} Writing Practice` : 'Writing Practice with AI Feedback'}
        description="Practice creative, persuasive, and informational writing with guided prompts and AI-powered feedback for grades 1–9."
        canonicalPath={grade ? `/writing/${grade}` : '/writing'}
      />
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <PenTool className="h-6 w-6 text-purple-600" />
                <h1 className="text-2xl font-bold text-gray-800">
                  Writing Pad - Grade {grade}
                </h1>
              </div>
            </div>
            
            {!showTopicSelector && selectedTopic && (
              <div className="flex gap-2">
                <Button
                  onClick={handleStartNew}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  New Topic
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving || !title.trim() || !content.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? "Saving..." : "Save Writing"}
                </Button>
              </div>
            )}
          </div>

          {showTopicSelector ? (
            <TopicSelector
              selectedGrade={grade || "1"}
              onTopicSelect={handleTopicSelect}
            />
          ) : (
            <WritingInterface
              topic={selectedTopic}
              grade={grade || "1"}
              subject={selectedSubject}
              writingType={selectedWritingType}
              onSubmit={handleWritingSubmit}
              onBack={handleBackToTopicSelection}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default WritingPad;
